import { useState } from "react";
import { TrendingUp, Pause, Play } from "lucide-react";

/**
 * Two-row marquee of realistic mini-CVs scrolling opposite directions.
 * 14 cards covering Kenyan + global names across roles + career stages.
 * Mixed scores (72-95) showing realistic before/after lift.
 * Hover-to-pause per row. Plus an animated "watch a score happen" SVG.
 */

const CVS = [
  {
    name: "Wanjiku Mwangi", role: "Senior Software Engineer", loc: "Nairobi, KE",
    bullets: [
      "Architected mobile-money rails processing 2.4M tx/day at 99.99% uptime",
      "Led 6-engineer team migrating monolith → 14 microservices in 8 months",
      "Cut p99 latency 62% via Redis caching + read-replica sharding",
    ],
    before: 64, after: 91, accent: "from-[#002FA7] to-[#1E40AF]",
  },
  {
    name: "Daniel Ochieng", role: "Product Designer", loc: "Nairobi, KE",
    bullets: [
      "Redesigned onboarding for a 480k MAU fintech — activation up 41%",
      "Built a 220-component design system adopted by 12 product teams",
      "Ran 70+ usability sessions across Kenya, Uganda, Tanzania",
    ],
    before: 59, after: 88, accent: "from-[#7C2D12] to-[#EF4444]",
  },
  {
    name: "Priya Patel", role: "Data Scientist", loc: "London, UK",
    bullets: [
      "Shipped LTV model lifting paid-acquisition ROAS by 2.1× ($4.8M ARR)",
      "Owned 9-feature recommender powering 38% of session revenue",
      "Built A/B framework now used across 4 product orgs",
    ],
    before: 67, after: 94, accent: "from-[#1E40AF] to-[#002FA7]",
  },
  {
    name: "Achieng' Odhiambo", role: "Marketing Lead", loc: "Mombasa, KE",
    bullets: [
      "Grew organic search traffic from 12k → 340k MAU in 14 months",
      "Cut paid CAC by 47% via creator partnerships across East Africa",
      "Launched Swahili content engine — 1.2M video views in Q1",
    ],
    before: 58, after: 86, accent: "from-[#7C2D12] to-[#B91C1C]",
  },
  {
    name: "Marcus Bennett", role: "VP Sales", loc: "San Francisco, US",
    bullets: [
      "Built outbound motion from $0 → $18M ARR in 22 months",
      "Hired + ramped 14 AEs at 84% quota attainment for 3 quarters",
      "Closed 3 of the company's top-10 logos (Microsoft, Stripe, Workday)",
    ],
    before: 71, after: 92, accent: "from-[#002FA7] to-[#3730A3]",
  },
  {
    name: "Naliaka Wekesa", role: "Operations Manager", loc: "Kampala, UG",
    bullets: [
      "Re-engineered last-mile logistics — delivery cost down 28% in 6 months",
      "Onboarded 1,400 riders across 4 cities with <2% churn quarterly",
      "Built ops dashboards now used by C-suite for weekly business review",
    ],
    before: 61, after: 87, accent: "from-[#B91C1C] to-[#EF4444]",
  },
  {
    name: "Aisha Khan", role: "Director of Engineering", loc: "Dubai, AE",
    bullets: [
      "Scaled engineering org from 18 → 76 across Nairobi + Dubai hubs",
      "Cut deploy lead-time from 4 days to 6 hours via trunk-based CI",
      "Owned platform reliability — 99.97% uptime across 3 product lines",
    ],
    before: 73, after: 95, accent: "from-[#3730A3] to-[#002FA7]",
  },
  {
    name: "Brian Kiprotich", role: "Junior Frontend Developer", loc: "Eldoret, KE",
    bullets: [
      "Built 6 React features for a 95k-user fitness app in 8 months",
      "Improved Lighthouse score from 64 → 96 via code-splitting + lazy load",
      "Open-source: 280 GitHub stars on a React component library",
    ],
    before: 52, after: 84, accent: "from-[#1E40AF] to-[#3730A3]",
  },
  {
    name: "Sofía Ramírez", role: "Growth PM", loc: "Mexico City, MX",
    bullets: [
      "Owned activation funnel — week-1 retention up 14 pts (32% → 46%)",
      "Ran 38 experiments shipping 11 wins worth $2.3M incremental ARR",
      "Built quarterly growth review — adopted as standard across 3 BU's",
    ],
    before: 66, after: 89, accent: "from-[#002FA7] to-[#1E40AF]",
  },
  {
    name: "Kwame Asante", role: "Mid-level Backend Engineer", loc: "Accra, GH",
    bullets: [
      "Migrated 4 services from REST to gRPC — p95 latency down 71%",
      "Owned payments retry logic recovering $1.1M/yr in failed charges",
      "Authored internal Go style guide adopted by 22 engineers",
    ],
    before: 60, after: 82, accent: "from-[#7C2D12] to-[#B91C1C]",
  },
  {
    name: "Lena Hofmann", role: "Senior UX Researcher", loc: "Berlin, DE",
    bullets: [
      "Led discovery for B2B SaaS redesign — NPS lifted from 14 → 47",
      "Built research repository (320 studies) saving PMs ~12 hrs/wk",
      "Mentored 5 junior researchers — 3 promoted within 14 months",
    ],
    before: 69, after: 90, accent: "from-[#3730A3] to-[#1E40AF]",
  },
  {
    name: "Faith Njeri", role: "Recent Graduate · Data Analyst", loc: "Nairobi, KE",
    bullets: [
      "Built Power BI dashboards for 4 SME clients during internship",
      "Won Kenya Data Hackathon 2025 — top 3 of 240 teams",
      "Self-taught SQL + Python: 14 projects on GitHub portfolio",
    ],
    before: 41, after: 72, accent: "from-[#B91C1C] to-[#EF4444]",
  },
  {
    name: "James O'Connor", role: "Engineering Manager", loc: "Dublin, IE",
    bullets: [
      "Managed 11 engineers across 2 squads — promotion rate 4× org avg",
      "Owned incident response — MTTR cut from 47 min → 11 min in 6 months",
      "Drove platform consolidation saving $480k/yr in infra cost",
    ],
    before: 70, after: 93, accent: "from-[#002FA7] to-[#3730A3]",
  },
  {
    name: "Zainab Hassan", role: "Career Switcher · Product Manager", loc: "Lagos, NG",
    bullets: [
      "Transitioned from finance — shipped first product in 5 months",
      "Owned merchant onboarding flow — TTV down from 6 days to 8 hours",
      "Built case study portfolio — landed PM role at YC-backed startup",
    ],
    before: 48, after: 78, accent: "from-[#7C2D12] to-[#EF4444]",
  },
];

// Split into two rows for opposite-direction marquee
const ROW_A = CVS.filter((_, i) => i % 2 === 0);
const ROW_B = CVS.filter((_, i) => i % 2 === 1);

function Avatar({ name }) {
  const initials = name.split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div className="w-10 h-10 bg-zinc-900 text-white flex items-center justify-center font-mono text-xs font-bold shrink-0">
      {initials}
    </div>
  );
}

function CVCard({ cv }) {
  const lift = cv.after - cv.before;
  return (
    <article
      className="w-[300px] md:w-[340px] shrink-0 bg-white border border-zinc-200 shadow-sm overflow-hidden flex flex-col"
      data-testid={`cv-card-${cv.name.replace(/[^a-z]/gi, "-").toLowerCase()}`}
    >
      <div className={`bg-gradient-to-br ${cv.accent} px-5 py-4 flex items-center gap-3`}>
        <Avatar name={cv.name} />
        <div className="text-white min-w-0">
          <div className="font-display font-bold text-sm truncate">{cv.name}</div>
          <div className="text-[11px] text-white/80 truncate">{cv.role} · {cv.loc}</div>
        </div>
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-400 mb-2">◷ EXPERIENCE · LAST ROLE</div>
        <ul className="space-y-2.5 text-[12px] text-zinc-700 leading-snug flex-1">
          {cv.bullets.map((b, i) => (
            <li key={i} className="flex gap-2">
              <span className="text-[#EF4444] font-mono text-[10px] mt-1 shrink-0">▸</span>
              <span>{b}</span>
            </li>
          ))}
        </ul>
        <div className="mt-5 pt-4 border-t border-zinc-200 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-baseline gap-1.5 tabular-nums">
              <span className="font-mono text-xs text-zinc-400 line-through">{cv.before}</span>
              <span className="text-zinc-400 text-xs">→</span>
              <span className="font-display font-extrabold text-2xl text-zinc-900">{cv.after}</span>
              <span className="text-xs text-zinc-400">/ 100</span>
            </div>
            <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-400 mt-0.5">BEFORE → AFTER</div>
          </div>
          <div className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#EF4444] text-white text-[11px] font-semibold shrink-0">
            <TrendingUp className="w-3 h-3" /> +{lift}
          </div>
        </div>
      </div>
    </article>
  );
}

function MarqueeRow({ items, direction = "left", paused, testid }) {
  // Duplicate items so the -50% translate creates seamless loop
  const doubled = [...items, ...items];
  return (
    <div className="overflow-hidden" data-testid={testid}>
      <div className={`marquee-track ${direction === "left" ? "marquee-left" : "marquee-right"} ${paused ? "marquee-paused" : ""}`}>
        {doubled.map((cv, i) => (
          <CVCard key={`${cv.name}-${i}`} cv={cv} />
        ))}
      </div>
    </div>
  );
}

export default function CVShowcase() {
  const [paused, setPaused] = useState(false);

  return (
    <section className="bg-zinc-50 border-b border-zinc-200" data-testid="cv-showcase">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <div className="overline mb-3">◇ AUDITED IN THE WILD</div>
            <h2 className="font-display font-extrabold text-3xl lg:text-5xl tracking-tight leading-[1.05] max-w-2xl">
              Real resumes.<br /><span className="brand-red">Real lift.</span>
            </h2>
            <p className="text-zinc-500 text-sm lg:text-base mt-4 max-w-xl">
              A live cross-section of audits — every card shows the candidate's score <strong className="text-zinc-700">before</strong> and <strong className="text-zinc-700">after</strong> one rewrite pass with ResumeLab.
            </p>
          </div>
          <button
            onClick={() => setPaused(p => !p)}
            data-testid="cv-showcase-toggle"
            aria-label={paused ? "Resume slider" : "Pause slider"}
            className="self-start md:self-auto inline-flex items-center gap-2 px-4 h-10 border border-zinc-300 hover:bg-zinc-900 hover:text-white hover:border-zinc-900 transition-colors print:hidden text-sm font-mono uppercase tracking-wider"
          >
            {paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
            {paused ? "Resume" : "Pause"}
          </button>
        </div>

        {/* TWO-ROW MARQUEE — opposite directions */}
        <div
          className="space-y-4 relative"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          {/* Edge fades */}
          <div className="pointer-events-none absolute inset-y-0 left-0 w-16 lg:w-24 bg-gradient-to-r from-zinc-50 to-transparent z-10" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-16 lg:w-24 bg-gradient-to-l from-zinc-50 to-transparent z-10" />

          <MarqueeRow items={ROW_A} direction="left" paused={paused} testid="cv-showcase-row-a" />
          <MarqueeRow items={ROW_B} direction="right" paused={paused} testid="cv-showcase-row-b" />
        </div>

        <div className="mt-6 text-center font-mono text-[11px] uppercase tracking-widest text-zinc-400">
          ◷ {CVS.length} of 120,000+ audits · hover to pause
        </div>

        {/* Animated SVG demo — CV being scored in real time */}
        <div className="mt-16 pt-16 border-t border-zinc-200">
          <div className="overline mb-3">◇ LIVE PROCESS · 12-SECOND LOOP</div>
          <h3 className="font-display font-extrabold text-2xl lg:text-3xl tracking-tight mb-2">
            Watch a score happen.
          </h3>
          <p className="text-zinc-500 text-sm mb-8 max-w-xl">
            Paste, parse, score, rewrite — the full 12-second arc, on loop.
          </p>
          <div className="bg-zinc-900 border border-zinc-800 p-6 lg:p-8 overflow-hidden" data-testid="cv-showcase-score-replay">
            <ScoreReplay />
          </div>
        </div>
      </div>
    </section>
  );
}

/** Pure-SVG "watch a resume get scored" replay loop. ~12s cycle. */
function ScoreReplay() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
      <svg viewBox="0 0 400 280" className="w-full h-auto" role="img" aria-label="Resume being scored animation">
        <rect x="20" y="20" width="360" height="240" fill="#fafafa" stroke="#e4e4e7" />
        {/* Header */}
        <rect x="36" y="34" width="0" height="10" fill="#18181b">
          <animate attributeName="width" values="0;120;120;120;0" keyTimes="0;0.08;0.92;0.96;1" dur="12s" repeatCount="indefinite" />
        </rect>
        <rect x="36" y="50" width="0" height="6" fill="#a1a1aa">
          <animate attributeName="width" values="0;70;70;70;0" keyTimes="0;0.1;0.92;0.96;1" dur="12s" repeatCount="indefinite" />
        </rect>
        {/* Body lines fill */}
        {[80, 96, 112, 128, 144, 160, 176, 192, 208].map((y, i) => (
          <rect key={y} x="36" y={y} width="0" height="6" fill="#d4d4d8">
            <animate
              attributeName="width"
              values={`0;${y % 32 ? 320 : 220};${y % 32 ? 320 : 220};0`}
              keyTimes={`0;${0.12 + i * 0.015};0.92;1`}
              dur="12s"
              repeatCount="indefinite"
            />
          </rect>
        ))}
        {/* Scanning sweep */}
        <rect x="20" y="20" width="360" height="2" fill="#EF4444" opacity="0">
          <animate attributeName="opacity" values="0;0.9;0.9;0" keyTimes="0;0.32;0.5;0.52" dur="12s" repeatCount="indefinite" />
          <animate attributeName="y" values="20;20;260;260" keyTimes="0;0.32;0.5;0.52" dur="12s" repeatCount="indefinite" />
        </rect>
        {/* Score badge */}
        <g>
          <rect x="280" y="36" width="86" height="48" fill="#002FA7" opacity="0">
            <animate attributeName="opacity" values="0;0;1;1;0" keyTimes="0;0.52;0.58;0.92;0.96" dur="12s" repeatCount="indefinite" />
          </rect>
          <text x="323" y="64" fill="#fff" fontFamily="ui-monospace, monospace" fontWeight="800" fontSize="20" textAnchor="middle" opacity="0">
            87
            <animate attributeName="opacity" values="0;0;1;1;0" keyTimes="0;0.52;0.58;0.92;0.96" dur="12s" repeatCount="indefinite" />
          </text>
          <text x="323" y="76" fill="#a5b4fc" fontFamily="ui-monospace, monospace" fontSize="6" textAnchor="middle" opacity="0">
            / 100
            <animate attributeName="opacity" values="0;0;1;1;0" keyTimes="0;0.52;0.58;0.92;0.96" dur="12s" repeatCount="indefinite" />
          </text>
        </g>
        {/* Rewrite shimmer on top 3 lines */}
        {[80, 96, 112].map((y, i) => (
          <rect key={`r-${y}`} x="36" y={y} width="320" height="6" fill="#EF4444" opacity="0">
            <animate
              attributeName="opacity"
              values="0;0;0.85;0;0"
              keyTimes={`0;${0.6 + i * 0.04};${0.62 + i * 0.04};${0.66 + i * 0.04};1`}
              dur="12s"
              repeatCount="indefinite"
            />
          </rect>
        ))}
        {/* +27 PTS badge final */}
        <g>
          <rect x="280" y="92" width="86" height="22" fill="#EF4444" opacity="0">
            <animate attributeName="opacity" values="0;0;1;1;0" keyTimes="0;0.76;0.8;0.92;0.96" dur="12s" repeatCount="indefinite" />
          </rect>
          <text x="323" y="107" fill="#fff" fontFamily="ui-monospace, monospace" fontWeight="800" fontSize="11" textAnchor="middle" opacity="0">
            +27 PTS
            <animate attributeName="opacity" values="0;0;1;1;0" keyTimes="0;0.76;0.8;0.92;0.96" dur="12s" repeatCount="indefinite" />
          </text>
        </g>
      </svg>

      <div>
        <ol className="space-y-4">
          {[
            { num: "01", t: "Parse", d: "PDF / DOCX / LinkedIn paste → structured text in 800ms.", color: "text-zinc-400" },
            { num: "02", t: "Score", d: "Match against 12 recruiter-grade signals + 10 ATS parsers.", color: "text-[#EF4444]" },
            { num: "03", t: "Rewrite", d: "Line-by-line GPT-5.2 rewrites only where you need them.", color: "text-white" },
            { num: "04", t: "Deliver", d: "Copy-ready resume + PDF + cover letter in your inbox.", color: "text-zinc-400" },
          ].map((step) => (
            <li key={step.num} className="flex gap-4 items-start">
              <div className="font-mono text-xs text-zinc-500 mt-1 shrink-0">{step.num}</div>
              <div>
                <div className={`font-display font-bold text-base ${step.color}`}>{step.t}</div>
                <div className="text-sm text-zinc-400 leading-relaxed">{step.d}</div>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
